import { Box, Typography, useTheme } from "@mui/material";
import { CreateFieldTexts } from "../../../utils/customerCreateFieldUtils";
import Map from "../../map/map.component";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  calculatePolygonCenter,
  handleKmlUpload,
  normalizeMapPosition,
} from "../../map/mapUtils";
import UnderlineButton from "../../buttons/underlineButton.component";
import CustomDialog from "../../muiDialogs/dialog.component";
import { normalizePolygonCoordinates } from "../../../utils/geoUtils";

const defaultFieldPosition = [40.0647, 9.1217];

const getPolygonFromData = (savedData, formData) => {
  if (Array.isArray(savedData?.polygonCoordinates)) {
    return savedData.polygonCoordinates;
  }

  if (Array.isArray(formData?.polygonCoordinates)) {
    return formData.polygonCoordinates;
  }

  if (Array.isArray(formData?.fieldMap?.polygonCoordinates)) {
    return formData.fieldMap.polygonCoordinates;
  }

  if (Array.isArray(formData?.Mappa?.polygonCoordinates)) {
    return formData.Mappa.polygonCoordinates;
  }

  if (Array.isArray(formData)) {
    return formData;
  }

  return [];
};

const normalizePolygon = (polygon) =>
  normalizePolygonCoordinates(polygon).map(({ lat, lng }) => [lat, lng]);

const arePolygonsEqual = (left, right) =>
  JSON.stringify(normalizePolygon(left)) ===
  JSON.stringify(normalizePolygon(right));

const getPolygonViewPosition = (polygon) =>
  calculatePolygonCenter(polygon) ||
  normalizeMapPosition(polygon?.[0]);

const getFieldViewPosition = (fieldPosition, polygon) =>
  getPolygonViewPosition(polygon) || normalizeMapPosition(fieldPosition);

const FieldMapForm = ({
  onDataChange,
  formData,
  onSubmit,
  savedData,
  isEditMode = false,
  onPolygonChange,
  pmtilesUrl,
  fieldPosition,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef();

  const initialPolygon = normalizePolygon(
    getPolygonFromData(savedData, formData),
  );
  const [polygonCoordinates, setPolygonCoordinates] = useState(initialPolygon);
  const [hasLocalPolygonChange, setHasLocalPolygonChange] = useState(false);
  const [error, setError] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const initialPolygonRef = useRef(initialPolygon);
  const latestPolygonRef = useRef(initialPolygon);
  const lastFieldViewPositionRef = useRef(
    getFieldViewPosition(fieldPosition, initialPolygon),
  );

  const handleChange = useCallback(
    (key, value) => {
      onDataChange?.({ [key]: value });
    },
    [onDataChange],
  );

  const handleSubmit = (event) => {
    event.preventDefault();

    const polygonToSubmit = Array.isArray(latestPolygonRef.current)
      ? latestPolygonRef.current
      : polygonCoordinates;

    if (polygonToSubmit.length === 0 && !isEditMode) {
      setError("Nessun poligono definito");
      return;
    }

    if (polygonToSubmit.length > 0 && polygonToSubmit.length < 3) {
      setError("Il poligono deve contenere almeno tre punti validi");
      return;
    }

    setPolygonCoordinates(polygonToSubmit);
    handleChange("polygonCoordinates", polygonToSubmit);

    if (onSubmit) {
      onSubmit({ polygonCoordinates: polygonToSubmit });
    }
    setError("");
  };

  const commitPolygonChange = useCallback(
    (
      newPolygon,
      { allowEmpty = false, showConfirmDialog = false } = {},
    ) => {
      const formattedPolygon = normalizePolygon(newPolygon);

      if (formattedPolygon.length === 0 && !isEditMode && !allowEmpty) {
        return;
      }

      latestPolygonRef.current = formattedPolygon;
      if (formattedPolygon.length > 0) {
        lastFieldViewPositionRef.current = getFieldViewPosition(
          fieldPosition,
          formattedPolygon,
        );
      }
      setPolygonCoordinates(formattedPolygon);
      setHasLocalPolygonChange(true);
      handleChange("polygonCoordinates", formattedPolygon);

      if (isEditMode) {
        onPolygonChange?.(
          !arePolygonsEqual(formattedPolygon, initialPolygonRef.current),
        );

        if (
          showConfirmDialog &&
          !arePolygonsEqual(formattedPolygon, initialPolygonRef.current)
        ) {
          setOpenDialog(true);
        }
      }
    },
    [fieldPosition, handleChange, isEditMode, onPolygonChange],
  );

  const handlePolygonCreated = (newPolygon) => {
    commitPolygonChange(newPolygon, { showConfirmDialog: true });
  };

  const handlePolygonDraftChange = useCallback(
    (newPolygon) => {
      const formattedPolygon = normalizePolygon(newPolygon);

      if (
        formattedPolygon.length === 0 ||
        arePolygonsEqual(formattedPolygon, latestPolygonRef.current)
      ) {
        return;
      }

      latestPolygonRef.current = formattedPolygon;
      lastFieldViewPositionRef.current = getFieldViewPosition(
        fieldPosition,
        formattedPolygon,
      );

      if (isEditMode) {
        setHasLocalPolygonChange(true);
        onPolygonChange?.(
          !arePolygonsEqual(formattedPolygon, initialPolygonRef.current),
        );
      }
    },
    [fieldPosition, isEditMode, onPolygonChange],
  );

  const handlePolygonDeleted = useCallback(() => {
    commitPolygonChange([], {
      allowEmpty: true,
      showConfirmDialog: isEditMode,
    });
  }, [commitPolygonChange, isEditMode]);

  const handleKmlPolygonChange = (uploadedPolygon) => {
    const formattedPolygon = normalizePolygon(uploadedPolygon);

    if (formattedPolygon.length === 0) {
      return;
    }

    latestPolygonRef.current = formattedPolygon;
    lastFieldViewPositionRef.current = getFieldViewPosition(
      fieldPosition,
      formattedPolygon,
    );
    setPolygonCoordinates(formattedPolygon);
    setHasLocalPolygonChange(true);
    handleChange("polygonCoordinates", formattedPolygon);

    if (isEditMode) {
      onPolygonChange?.(
        !arePolygonsEqual(formattedPolygon, initialPolygonRef.current),
      );
      setOpenDialog(true);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleKmlUploadCallback = (event) => {
    handleKmlUpload(event, (uploadedPolygon) => {
      handleKmlPolygonChange(uploadedPolygon);
    }, setError);
  };

  const handleCloseDialog = () => {
    latestPolygonRef.current = initialPolygonRef.current;
    lastFieldViewPositionRef.current = getFieldViewPosition(
      fieldPosition,
      initialPolygonRef.current,
    );
    setPolygonCoordinates(initialPolygonRef.current);
    setHasLocalPolygonChange(false);
    handleChange("polygonCoordinates", initialPolygonRef.current);
    onPolygonChange?.(false);
    setOpenDialog(false);
  };

  const handleConfirmDialog = () => {
    setPolygonCoordinates(latestPolygonRef.current);
    handleChange("polygonCoordinates", latestPolygonRef.current);
    setOpenDialog(false);
  };

  useEffect(() => {
    const nextPolygon = normalizePolygon(
      getPolygonFromData(savedData, formData),
    );
    const nextFieldViewPosition = getFieldViewPosition(
      fieldPosition,
      nextPolygon,
    );

    if (nextFieldViewPosition) {
      lastFieldViewPositionRef.current = nextFieldViewPosition;
    }

    if (!arePolygonsEqual(nextPolygon, initialPolygonRef.current)) {
      initialPolygonRef.current = nextPolygon;
      latestPolygonRef.current = nextPolygon;
      setPolygonCoordinates(nextPolygon);
      setHasLocalPolygonChange(false);
      if (isEditMode) {
        onPolygonChange?.(false);
      }
      return;
    }

    if (hasLocalPolygonChange) {
      return;
    }

    if (!arePolygonsEqual(nextPolygon, latestPolygonRef.current)) {
      latestPolygonRef.current = nextPolygon;
      setPolygonCoordinates(nextPolygon);
    }
  }, [
    savedData,
    formData,
    hasLocalPolygonChange,
    isEditMode,
    onPolygonChange,
    fieldPosition,
  ]);

  const explicitFieldViewPosition = normalizeMapPosition(fieldPosition);
  const mapFieldPosition =
    explicitFieldViewPosition ||
    lastFieldViewPositionRef.current ||
    defaultFieldPosition;
  const hasFieldViewPosition = Boolean(
    explicitFieldViewPosition || lastFieldViewPositionRef.current,
  );
  const hasPolygonCoordinates = polygonCoordinates.length >= 3;

  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      gap={2}
      component="form"
      id="map-form"
      onSubmit={handleSubmit}
    >
      <Typography fontSize="12px" color={theme.palette.custom.infoTextColor}>
        {CreateFieldTexts.mapSection}
      </Typography>
      <Box width="100%">
        <UnderlineButton
          onClick={handleButtonClick}
          label="Preferisco caricare il file kml"
        />
        <input
          type="file"
          accept=".kml"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleKmlUploadCallback}
        />
      </Box>
      <Box width="100%" height="431px" borderRadius={2} position="relative">
        <Map
          isDrawable={true}
          onPolygonCreated={handlePolygonCreated}
          onPolygonDraftChange={handlePolygonDraftChange}
          onPolygonDeleted={handlePolygonDeleted}
          allowPolygonDelete={true}
          polygonCoordinates={polygonCoordinates}
          pmtilesUrl={pmtilesUrl}
          zoomLevel={hasPolygonCoordinates || !hasFieldViewPosition ? 7 : 16}
          fieldPosition={mapFieldPosition}
        />
      </Box>
      {error && (
        <Typography fontSize="12px" color="red">
          {error}
        </Typography>
      )}
      <CustomDialog
        open={openDialog}
        onConfirm={handleConfirmDialog}
        onClose={handleCloseDialog}
        title="Sei sicuro di voler modificare il terreno?"
        description="La modifica del terreno comporta anche la ridefinizione dei sottoterreni"
      />
    </Box>
  );
};

export default FieldMapForm;
